<template>
  <div class="grid-card sign_up_initiated" @click="handleCardClick" tabindex="-1"
    :aria-label="`Bond by ${issuerName}, yield ${yieldRate}% per annum, click to view details`">
    <div class="card-background">
      <div class="gradient-overlay"></div>
    </div>

    <div class="card-content">
      <div class="header-row">
        <div class="bank-chip">
          <div class="bank-logo" v-if="issuerLogo" style="width: 18px; height: 18px; display: flex; align-items: center; justify-content: center; overflow: hidden; flex-shrink: 0;">
            <img :src="issuerLogo" :alt="displayName" class="logo" width="18" height="18" style="width: 18px; height: 18px; object-fit: contain;" loading="lazy" />
          </div>
          <span  class="bank-name" :aria-label="`Issuer: ${issuerName || name}`">{{ issuerName || name }}</span>
        </div>
        <div class="product-card__badge" v-if="badge" :aria-label="`Badge: ${badge.label}`">
          <span :class="['badge', `badge--${badge.variant}`]" aria-hidden="true">
            <div class="icon-container" style="display: flex; align-items: center; justify-content: center; width: 14px; height: 14px;">
              <img :src="badge_icons[badge.variant]" class="badge-icon" :alt="`${badge.label} Icon`" width="14" height="14" style="width: 14px; height: 14px; object-fit: contain;" loading="lazy" />
            </div>
            {{ badge.label }}
          </span>
        </div>
      </div>

      <div class="rating-row" v-if="category || isin" aria-label="Risk and category tags">
        <span v-if="isin" class="isin-chip" :aria-label="`ISIN: ${isin}`">
          {{ isin }}
        </span>

        <span v-if="category" class="tiny-chip category-style" :aria-label="`Category: ${category}`">
          {{ category }}
      </span>
      </div>

      <div class="main-content-row">
        <div class="rate-section">
          <div>
            <span class="rate-value" >{{ yieldRate }}%</span>
          </div>
          <div class="upto-text" :aria-label="yieldLabel">{{ yieldLabel }}</div>
        </div>
      </div>

      <div class="details-panel">
        <div class="detail-item" :aria-label="`Minimum Investment: ${formattedMinAmount}`">
          <span class="detail-text" >Min : <b>{{
              formattedMinAmount }}</b></span>
        </div>
        <div class="detail-item" :aria-label="`Payouts: ${payouts}`">
          <div class="divider" aria-hidden="true"></div>
          <span class="detail-text"  :aria-label="`Payouts : ${payouts}`">Payouts <b>{{ payouts
              }}</b></span>
        </div>
        <div class="detail-item" :aria-label="`Maturity Date: ${formatDateToDDMonYY(maturityDate)}`">
          <div class="divider" aria-hidden="true"></div>
          <span class="detail-text"
            :aria-label="`Maturity Date : ${formatDateToDDMonYY(maturityDate)}`">Maturity <b>{{
              formatDateToDDMonYY(maturityDate) }}</b></span>
        </div>
      </div>

      <div v-if="creditRiskMeter" class="card-crom-container" style="margin-top:12px; margin-bottom:12px; width: 100%;">
        <CreditRiskMeter :meter="creditRiskMeter" variant="card" :show-disclaimer="false" :security-label="computedSecurityLabel" />
      </div>

      <div class="card-footer">
        <CommonButton class="know-more-btn sign_up_initiated" text="Know More"
          :ariaLabel="`Know more about ${displayName} bond`" :color="'#0027AF'" width="96px" height="36px" fontSize="12px"
          :href="props.bondId ? undefined : investEquirizeUrl" :to="bondDetailsTo" :action="() => emit('knowMore', props)"
          @click.stop />

        <span
          v-if="mappedSecurityInfo.displayName"
          class="seniority-chip"
          :class="mappedSecurityInfo.chipColorClass"
          aria-label="Seniority"
        >
          <img :src="mappedSecurityInfo.chipColorClass === 'seniority-chip--red' ? unsecuredBondSparkleIcon : seniorSecuredBondSparkleIcon" alt="" aria-hidden="true" width="12" height="13" style="width: 12px; height: 13px; display: block;" />
          {{ mappedSecurityInfo.displayName }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import seniorSecuredBondSparkleIcon from "@/assets/icons/bond-details-v2/senior-secured-bond-sparkle.svg";
import unsecuredBondSparkleIcon from "@/assets/icons/bond-details-v2/unsecured-bond-sparkle.svg";
import bond_icon_closing from "@/assets/images/icons/bond_rating/bond_product_closing.svg";
import bond_icon_high from "@/assets/images/icons/bond_rating/bond_product_high.svg";
import bond_icon_limited from "@/assets/images/icons/bond_rating/bond_product_limited.svg";
import bond_icon_new from "@/assets/images/icons/bond_rating/bond_product_new.svg";
import bond_icon_trending from "@/assets/images/icons/bond_rating/bond_product_treanding.svg";
import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useUtmTracking } from "~/composables/useUtmTracking";
import { getBondDetailsUrl, getDisplayRatingTags, isValidCreditRating, resolveSecurityInfo } from "~/utils/common";
import CreditRiskMeter from "./CreditRiskMeter.vue";

defineOptions({ name: "BondProductCard" });

const { buildRedirectUrl } = useUtmTracking();
const route = useRoute();
const router = useRouter();

export interface Badge {
  label: string;
  variant: "new" | "limited" | "high" | "trending" | "closing";
}

export interface Props {
  issuerName: string;
  name?: string;
  issuerLogo?: string;
  rating?: string;
  creditRiskMeter?: any;
  category?: string;
  seniority?: string;
  issuanceType?: string;
  yieldRate: string | number;
  yieldLabel?: string;
  minAmount: string;
  payouts: string;
  tenure?: string;
  maturityDate: string;
  badge?: Badge;
  tags?: string[];
  bondId?: string;
  isin?: string;
}


const badge_icons: any = {
  new: bond_icon_new,
  limited: bond_icon_limited,
  high_yield: bond_icon_high,
  trending: bond_icon_trending,
  closing: bond_icon_closing
}

const props = withDefaults(defineProps<Props>(), {
  yieldLabel: "Yield to Maturity",
  name: undefined,
  issuerLogo: undefined,
  rating: "",
  creditRiskMeter: undefined,
  category: "",
  seniority: "",
  issuanceType: "",
  tenure: "",
});

const emit = defineEmits<{
  (e: "knowMore", product: Props): void;
  (e: "cardClick", product: Props): void;
}>();

const displayName = computed(() => props.name?.trim() || props.issuerName);
const { investorUrl } = useInvestorPlatform();

const investEquirizeUrl = computed(() => {
  const ctaName = `know-more-${displayName.value?.toLowerCase().replace(/\s+/g, '-') || 'bond'}-card`;
  return buildRedirectUrl(investorUrl("/login"), route.path, ctaName);
});

const bondDetailsTo = computed(() => {
  if (!props.bondId || !props.isin) return undefined;
  return getBondDetailsUrl(props.isin, displayName.value);
});

const computedSecurityLabel = computed(() => {
  return resolveSecurityInfo(props.issuanceType, props.seniority).displayName
})

interface SecurityInfo {
  displayName: string;
  chipColorClass: "seniority-chip--green" | "seniority-chip--red";
}

const mappedSecurityInfo = computed<SecurityInfo>(() => {
  return resolveSecurityInfo(props.issuanceType, props.seniority)
});

const showSeniorSecuredTag = computed(() => {
  if (mappedSecurityInfo.value.chipColorClass === "seniority-chip--red") {
    return false;
  }
  const hasSeniority = String(mappedSecurityInfo.value.displayName || "").toLowerCase().includes("senior");
  const hasTag = (props.tags || []).some((tag) => {
    const normalizedTag = String(tag).trim().toLowerCase();
    return normalizedTag === "senior secured bond" || normalizedTag === "senior secured" || normalizedTag === "senior";
  });
  return hasSeniority || hasTag;
});

const formattedMinAmount = computed(() => {
  const value = String(props.minAmount || "").trim();
  if (!value || value.toLowerCase() === "n/a") return value || "N/A";

  const amount = Number(value.replace(/[₹,\s]/g, ""));
  if (!Number.isFinite(amount)) return value;

  return `₹${amount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
});

const handleCardClick = () => {
  emit("cardClick", props);
  const url = bondDetailsTo.value;
  if (url) {
    router.push(url);
  } else {
    window.open(investEquirizeUrl.value, "_blank");
  }
};

function formatDateToDDMonYY(dateStr: string): string {
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return dateStr;
  const day = String(date.getDate()).padStart(2, "0");
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = String(date.getFullYear()).slice(-2);
  return `${day} ${month} ${year}`;
}

</script>

<style scoped>
.grid-card {
  position: relative;
  width: 100%;
  height: auto;
  min-height: 190px;
  max-width: 450px;
  background: linear-gradient(180deg, #FFFFFF 25.96%, #C6E9FF 64.9%);
  border-radius: 7px;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #D5EFFF;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;

}

.grid-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-lg);
}

.card-background {
  position: absolute;
  inset: 0;
  overflow: hidden;
}

.gradient-overlay {
  position: absolute;
  inset: 0;
  background: transparent;
}

.card-content {
  position: relative;
  z-index: 1;
  padding: 16px 12px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 6px;
  width: 100%;
  min-width: 320px;
  box-sizing: border-box;
}

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  margin-bottom: 0;
}

.bank-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: 4px;
  padding: 2px 8px 2px 2px;
  flex: 1;
  min-width: 0;
  margin-right: 8px;
}

.rate-unit {
  color: var(--primaryText);
}

.bank-logo {
  width: 18px;
  height: 18px;
  border-radius: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.logo {
  width: 18px;
  height: 18px;
  object-fit: contain;
}

.bank-name {
  display: block;
  min-width: 0;
  max-width: 100%;
  font-family: var(--font-family-primary);
  font-size: 15px;
  font-weight: 500;
  color: var(--primaryText);
  line-height: 24px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rating-row {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.isin-chip {
  font-family: var(--font-family-primary);
  font-size: 10px;
  font-weight: 700;
  padding: 4px 6px;
  border-radius: 4px;
  background: #f5f5f5;
  color: #5f6685;
  white-space: nowrap;
  line-height: 1;
}



.tiny-chip {
  display: inline-block;
  display: flex;
  font-size: 10px;
  size: 10px;
  line-height: 100%;
  text-align: center;
  font-weight: 700;
  padding: 4px;
  border-radius: 4px;
  background: #eff9ff;
  white-space: nowrap;
  color: #005dab;
}
.rating-chips{
  display: flex;
  gap: 4px;
}

.tiny-chip p {
  font-weight: 700;
  display: flex;
  justify-content: center;
  align-items: center;
}

.tag-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  margin-right: 8px;
}

.tag-icon :deep(svg) {
  width: 100%;
  height: 100%;
  display: block;
}

.product-card__badge {
  z-index: 2;
  flex-shrink: 0;
}

.badge {
  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;
  top: -16px;
  right: 10px;
  padding: 6px 4px;
  border-radius: 0 0 4px 4px;
  font-family: var(--font-family-primary);
  font-size: 10px;
  font-weight: 600;
  line-height: 12px;

}

.icon-container {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px 0;
}

.badge--new,
.badge--high,
.badge--trending {
  background: #EAF7FF;
  color: #0027AF;
}

.badge--limited,
.badge--closing {
  background: #F9DEDC;
  color: #B3261E;
}

.main-content-row {
  display: flex;
  width: 100%;
}

.rate-section {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.chips-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-end;
}

.senior-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: var(--color-primary-90);
  border-radius: 4px;
  padding: 5px 8px;
}

.senior-text {
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 500;
  color: var(--primaryText);
  line-height: 13.31px;
  letter-spacing: -0.165px;
  white-space: nowrap;
}

.upto-text {
  font-weight: 500;
  font-size: 13px;
  line-height: 15px;
  color: #1b2559;
  font-weight: 500;
}

.rate-value {
  font-family: var(--font-family-primary);
  font-size: 22px;
  font-weight: 1000;
  color: #0027af;
  line-height: 24px;
}

.details-panel {
  display: flex;
  align-items: center;
  gap: 0;
  background: #fff;
  border-radius: 4px;
  padding: 8px;
  width: fit-content;
  max-width: 100%;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.detail-item:not(:last-child) {
  margin-right: 10px;
}

.divider {
  width: 2px;
  height: 15px;
  background: #1b2559;
  border-radius: 99px;
}

.detail-text {
  font-family: var(--font-family-primary);
  font-size: 12px;
  font-weight: 400;
  color: var(--primaryText);
  line-height: 16.8px;
  white-space: nowrap;
  display: flex;
  gap: 2px;
}

.detail-text-highlighted {
  font-weight: 700;
}

.know-more-btn {
  border-radius: 6px !important;
  padding: 9.5px 12px !important;
  font-size: 12px !important;
  font-weight: 500 !important;
  line-height: 16.5px !important;
  height: auto !important;
  width: 92px !important;
  min-height: 32px;
  margin-top: 0;
  align-self: flex-start;
}

.card-footer {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 8px;
  margin-top: 10px;
}

.seniority-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 24px;
  padding: 4px 8px;
  border-radius: 4px;
  font-family: var(--font-family-primary);
  font-size: 12px;
  font-weight: 500;
  line-height: 14px;
  white-space: nowrap;
  margin-left: auto;
}

.seniority-chip--green {
  background: #e9ffe6;
  color: #006e35;
}

.seniority-chip--red {
  background: #f9dedc;
  color: #b3261e;
}

.seniority-chip img {
  width: 12px;
  height: 13px;
  display: block;
}

@media (max-width: 480px) {

  .bank-name {
    font-size: 15px;
    width: 100%;
    text-overflow: clip;

  }

  .detail-item {
    margin: 0 2px;
  }

  .detail-text {
    font-size: 11px;
    font-weight: 400;
    line-height: 11.8px;
    white-space: normal;
    gap: 1;
    word-break: break-word;
  }

  .details-panel {
    padding: 4px;
  }

  .card-content {
    width: 100%;
  }


  .grid-card {
    height: auto;
    min-height: auto;
  }

  .card-content {
    padding: 12px;
  }

  .know-more-btn {
    min-height: 32px;
    height: auto;
    width: 96px;
  }

  .seniority-chip {
    font-size: 10px;
    padding: 4px 6px;
  }
}

@media (max-width:400px) {
  .detail-text {
    font-size: 9px;
  }
}

/* Tablet / above mobile: keep card at 450px */
@media (min-width: 481px) and (max-width: 1023px) {
  .grid-card {
    width: 450px;
    max-width: 450px;
  }
}
</style>
